import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * The keyboard-safe sheet contract, in one place.
 *
 * Everything here was learned on a phone over ~15 commits (a1b649b → 2f932f3,
 * Aug 19-21 2026) and lived inside BottomSheet.tsx until Sep 1 2026, when the
 * Party Planner wizard turned out to need the same guarantees and to have none
 * of them — its panel was `height: 92dvh`, so the keyboard covered the bottom
 * ~300px of a scrollport that had no range to give, and the theme "Something
 * else" box could not be reached by any amount of scrolling.
 *
 * A second copy of these rules would have drifted from the first within a
 * week. One module, two consumers, one set of constants.
 *
 * The root fact everything follows from: iOS raw-closes and reopens the
 * keyboard between EVERY field focus (the "F:0 blip"). `vv.iH` flaps
 * 577↔878 across focus changes while the layout viewport stays at full height
 * — WKWebView does not shrink it. So `dvh`, `vh`, `%`-of-viewport and
 * `window.innerHeight` are all disqualified as a basis: they re-layout on a
 * different clock from the frozen px offset, and two clocks read as jumping.
 */

/**
 * Gap left between a rescued input's bottom edge and the STABLE keyboard top.
 *
 * 56, not the original 12, and the reason is what broke the last field once
 * the rescue became the only scroller (Aug 20). `keyboardTop` derives from the
 * FROZEN stable offset, and the freeze deliberately drops all raw wobble once
 * the phase is "open" — including the QuickType predictive bar (~44px), which
 * appears over text fields but not over the picker wheels. If the offset froze
 * without the bar, the real keyboard top sits ~44px ABOVE keyboardTop, and a
 * field revealed to keyboardTop-12 is still under the keys. WebKit's native
 * reveal used to paper over this because UIKit knows the true keyboard frame;
 * suppressing it exposed our margin as too thin. 44 (bar) + 12 (breathing
 * room) = 56.
 */
export const RESCUE_MARGIN_PX = 56;
/**
 * Keyboard-open bottom padding of the scrollport, and it MUST exceed
 * RESCUE_MARGIN_PX — this is the LAST field's entire scroll range. At max
 * scrollTop the last field's bottom sits exactly `padding` above the
 * scrollport bottom (= keyboardTop), so if padding < margin the rescue's
 * target is beyond the scroll clamp and the last field can NEVER be revealed,
 * no matter how many passes run. Derived, not independent, so the invariant
 * cannot drift.
 */
export const SCROLL_PADDING_OPEN_PX = RESCUE_MARGIN_PX + 16;
/**
 * Consecutive frames the focused element's rect must be UNCHANGED before the
 * rescue acts. iOS runs its own scroll-into-view and the keyboard slide-up
 * concurrently with focus, both lasting ~250ms. Measuring one frame after
 * focusin — as the first version of this did — reads a rect mid-flight,
 * scrolls by a wrong delta, and then iOS corrects on top of it. Two agents
 * scrolling the same container a quarter second apart is precisely the
 * residual jitter on field hops that this replaced.
 */
const RESCUE_SETTLE_FRAMES = 2;
/** Ceiling, ~0.5s at 60fps, so a continuously animating element cannot pin the loop. */
const RESCUE_MAX_FRAMES = 30;
/**
 * After scrolling, the rescue re-measures and scrolls again if the field is
 * still under the keyboard — up to this many passes. One pass was enough only
 * while WebKit's native reveal ran underneath as a backstop; alone, a single
 * pass silently under-delivers when the first write is clamped mid-layout or
 * something (the OS, a late layout) moves the container between frames.
 */
const RESCUE_MAX_PASSES = 3;

/**
 * Sheet geometry, in px, from values that cannot blink.
 *
 * Aug 20 2026, third pass. The previous two used CSS bases that both move
 * independently of the keyboard actually being up:
 *
 *   a1b649b  top AND height from var(--vv-height)  -> whole panel translated
 *   a8e5eb1  overlay height from var(--vv-height)  -> header held still, but
 *            the overlay still resized on every blip
 *
 * Device ev() logs settled it: iOS raw-closes and reopens the keyboard between
 * EVERY field (F:0 blip, then T:301). `stbl` holds 301 throughout — the
 * hysteresis in useStableKeyboardOffset works — but Index.tsx releases
 * --vv-height on the RAW close by design (:799-823, un-debounced, so chat
 * dismiss feels immediate). So the var round-trips on every field hop while
 * the keyboard never actually leaves, and anything consuming it resizes.
 *
 * That also explains the stranded inputs. At tap time the sheet is transiently
 * full-height, so the focused input IS inside the scrollport and WebKit's
 * scroll-on-focus correctly declines; the sheet then shrinks back and strands
 * it under the keys. See useSheetFocusRescue for the belt-and-suspenders half.
 *
 * Fix: consume the two hysteresis-protected px values directly and stop
 * reading the var or any viewport unit.
 *
 *   fullHeight       useStableFullHeight  — sampled only while closed, frozen
 *   keyboardOffset   useStableKeyboardOffset — frozen once phase is "open"
 *
 * Neither changes during an F:0 blip, so the blip produces ZERO layout change.
 * --vv-height keeps its raw-release behaviour for the shell and chat; the
 * sheet simply stops consuming it.
 *
 *                     closed (K=0)        open (K=301)
 *     top                 70                  70        <- constant
 *     overlay height     878                 577        = keyboard top
 *     panel height       808 (bottom:0)      507
 *
 * `top` depends only on the frozen fullHeight, never on the offset, which is
 * what keeps the header pixel-stationary across field hops.
 */
export function sheetKeyboardGeometry(fullHeight: number, keyboardOffset: number) {
  // Same threshold Index.tsx:357 uses. Below 80px the value is the focus
  // sentinel or the accessory bar, not a keyboard.
  const keyboardOpen = keyboardOffset > 80;

  const visible = keyboardOpen ? fullHeight - keyboardOffset : fullHeight;
  // Safety clamp mirroring Index.tsx:764 — a bogus offset can never collapse
  // the sheet past half the screen; degrade to full height instead.
  const safeVisible = visible < fullHeight * 0.5 ? fullHeight : visible;

  return {
    // The only value that tracks the keyboard. px, from the frozen offset.
    overlayHeight: `${safeVisible}px`,
    // Constant: 8% of the frozen full height, independent of the offset.
    top: `${Math.round(fullHeight * 0.08)}px`,
    // Viewport-space y of the keyboard's top edge, for the focusin rescue.
    keyboardTop: safeVisible,
    keyboardOpen,
    // Open padding is the last field's entire scroll range — see
    // SCROLL_PADDING_OPEN_PX for why it must exceed the rescue margin. Still
    // a fixed constant, never a keyboardOffset term: a lift regrown here is
    // the Aug 4 double-subtract. Closed padding unchanged from July 21
    // (5ceebf9 / #543).
    scrollPaddingBottom: keyboardOpen
      ? `${SCROLL_PADDING_OPEN_PX}px`
      : "calc(30px + env(safe-area-inset-bottom))",
  };
}

/**
 * Which elements get the single-scroller focus interception below.
 *
 * Text-like fields only. Date/time inputs keep native handling — their tap
 * opens a picker wheel, and intercepting the mousedown risks the wheel not
 * presenting at all. Buttons, checkboxes and everything else are untouched.
 */
const INTERCEPTED_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url",
  "number",
  "password",
]);

export function isInterceptedTextField(el: unknown): el is HTMLElement {
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return INTERCEPTED_INPUT_TYPES.has(el.type);
  return false;
}

/**
 * SINGLE-SCROLLER RULE (Aug 20), as a ready-made mousedown handler.
 *
 * WebKit's own scroll-on-focus and our settle-based rescue were both moving
 * the scroll container — two movers a quarter-second apart is the last
 * residual jitter on field hops. Intercept the synthesized mousedown for text
 * fields and focus with preventScroll, so the rescue is the ONLY thing that
 * ever scrolls the container. The rescue re-runs when the stable offset
 * settles (keyboardTop is in its dep list), so every tapped field still ends
 * up visible — that guarantee is what makes suppressing the native reveal
 * safe.
 *
 * mousedown, not touchstart (preventDefault there would kill drag scrolling)
 * and not touchend (would kill click). Synthesized mouse events only fire for
 * taps, never for scroll drags, so scrolling by dragging on a field is
 * unaffected.
 *
 * Known tradeoff: the FIRST tap into an already-filled field puts the caret at
 * the end instead of the tap point (programmatic focus cannot place a caret).
 * A second tap — the field is now focused, so we stand aside — repositions
 * natively. Already-focused fields are never intercepted for exactly that
 * reason.
 */
export function interceptTextFieldMouseDown(e: { target: unknown; preventDefault: () => void }) {
  const target = e.target;
  if (!isInterceptedTextField(target)) return;
  if (target === document.activeElement) return;
  e.preventDefault();
  target.focus({ preventScroll: true });
}

/**
 * Belt-and-suspenders for the stranded-input case.
 *
 * Runs when a new focus lands inside `rootRef` AND again once the stable
 * offset settles, because at tap time the keyboard height is not yet known —
 * that ordering is the whole reason WebKit's own scroll-on-focus declines and
 * the field ends up covered.
 *
 * `rootRef` is the whole sheet (so chrome focus is recognised and ignored);
 * `scrollRef` is the one container allowed to move.
 */
export function useSheetFocusRescue({
  rootRef,
  scrollRef,
  keyboardTop,
  keyboardOpen,
}: {
  rootRef: RefObject<HTMLElement>;
  scrollRef: RefObject<HTMLElement>;
  keyboardTop: number;
  keyboardOpen: boolean;
}) {
  // The element that most recently took focus inside this sheet, plus a
  // counter so re-focusing the SAME element still re-runs the rescue.
  const focusedRef = useRef<HTMLElement | null>(null);
  const [focusTick, setFocusTick] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // focusin, not focus: focus does not bubble, and the inputs are arbitrary
    // depths inside `children`. Sheet-local listener, removed on unmount —
    // ~29 sheets exist in TasksTab but only the open one mounts this shell.
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && root.contains(target)) {
        focusedRef.current = target;
        setFocusTick((n) => n + 1);
      }
    };
    root.addEventListener("focusin", onFocusIn);
    return () => root.removeEventListener("focusin", onFocusIn);
  }, [rootRef]);

  useEffect(() => {
    const el = focusedRef.current;
    const scroller = scrollRef.current;
    if (!el || !scroller || !keyboardOpen) return;

    let frame = 0;
    let frames = 0;
    let settled = 0;
    let passes = 0;
    let lastBottom = Number.NaN;

    // Wait for the element to STOP moving before deciding anything. This is
    // the "once the stable offset settles" half — acting on the first frame
    // means competing with iOS's own scroll rather than finishing after it.
    const step = () => {
      if (!scroller.contains(el)) return;
      const bottom = el.getBoundingClientRect().bottom;
      if (bottom === lastBottom) settled += 1;
      else {
        settled = 0;
        lastBottom = bottom;
      }
      frames += 1;

      if (settled < RESCUE_SETTLE_FRAMES && frames < RESCUE_MAX_FRAMES) {
        frame = requestAnimationFrame(step);
        return;
      }

      const overflow = bottom - (keyboardTop - RESCUE_MARGIN_PX);
      // Only ever scroll DOWN toward the field. Negative overflow means it is
      // already clear of the keyboard and nothing should move.
      if (overflow > 0 && passes < RESCUE_MAX_PASSES) {
        scroller.scrollTop += overflow;
        passes += 1;
        // Verify rather than trust: re-measure over the next frames and
        // scroll the remainder if the write was clamped or something moved
        // the container. Settle counters reset so the re-measure is honest.
        settled = 0;
        lastBottom = Number.NaN;
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [focusTick, keyboardTop, keyboardOpen, scrollRef]);
}
